// src/components/UserMeet/User-Meet-Private/InviteToBreakoutModal.jsx
import React, { useState, useMemo } from "react";
import { X, UserPlus, Users, Search, Check, Info } from "lucide-react";

export default function InviteToBreakoutModal({
  open,
  onClose,
  participants,
  onInvite,
  inviting,
  alreadyInvited = [],
}) {
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredParticipants = useMemo(() => {
    if (!participants) return [];
    
    const query = searchQuery.toLowerCase().trim();
    if (!query) return participants;

    return participants.filter(
      (p) =>
        p.name?.toLowerCase().includes(query) ||
        p.email?.toLowerCase().includes(query)
    );
  }, [participants, searchQuery]);

  const categorizedParticipants = useMemo(() => {
    const available = [];
    const invited = [];

    filteredParticipants.forEach((p) => {
      const userId = String(p.userId || p.id);
      if (alreadyInvited.includes(userId)) {
        invited.push(p);
      } else {
        available.push(p);
      }
    });

    return { available, invited };
  }, [filteredParticipants, alreadyInvited]);

  const toggleSelection = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleInvite = () => {
    if (selectedUserIds.length === 0) return;
    onInvite(selectedUserIds);
    setSelectedUserIds([]);
  };

  const handleClose = () => {
    setSelectedUserIds([]);
    setSearchQuery("");
    onClose();
  };

  if (!open) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-[9999] p-5 animate-fadeIn"
      onClick={handleClose}
    >
      <div 
        className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-green-500/10 animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-white/10 flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 flex-1">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500/15 to-emerald-500/15 border border-green-500/30 flex items-center justify-center flex-shrink-0">
              <UserPlus className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white mb-1">Invite Participants</h2>
              <p className="text-sm text-white/60">Select people to invite to your breakout room</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={inviting}
            className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:bg-red-500/15 hover:border-red-500/30 hover:text-red-400 transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto flex-1">
          {/* Search */}
          <div className="relative mb-4">
            <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
            <input
              type="text"
              placeholder="Search participants..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={inviting}
              className="w-full pl-11 pr-10 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder:text-white/40 focus:outline-none focus:border-green-500/50 focus:bg-white/8 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                type="button"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md bg-white/10 hover:bg-red-500/15 hover:text-red-400 text-white/60 flex items-center justify-center transition-all"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Selection Summary */}
          {selectedUserIds.length > 0 && (
            <div className="flex items-center gap-2.5 px-3.5 py-2.5 bg-green-500/10 border border-green-500/25 rounded-xl mb-4 text-sm text-green-400 font-semibold">
              <Users size={16} className="flex-shrink-0" />
              <span className="flex-1">
                {selectedUserIds.length} participant{selectedUserIds.length !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={() => setSelectedUserIds([])}
                type="button"
                className="px-2.5 py-1 bg-white/10 border border-white/15 rounded-md text-white/80 text-xs font-semibold hover:bg-red-500/15 hover:border-red-500/30 hover:text-red-400 transition-all"
              >
                Clear
              </button>
            </div>
          )}

          {/* Participant List */}
          <div className="max-h-96 overflow-y-auto pr-1 space-y-4">
            {/* Available Participants */}
            {categorizedParticipants.available.length > 0 && (
              <div>
                <div className="text-xs font-bold text-white/50 uppercase tracking-wide mb-2.5 px-1">
                  Available ({categorizedParticipants.available.length})
                </div>
                <div className="space-y-2">
                  {categorizedParticipants.available.map((p) => {
                    const userId = String(p.userId || p.id);
                    const isSelected = selectedUserIds.includes(userId);

                    return (
                      <div
                        key={userId}
                        onClick={() => !inviting && toggleSelection(userId)}
                        className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                          isSelected
                            ? 'bg-green-500/10 border-green-500/40'
                            : 'bg-white/3 border-white/10 hover:bg-white/6 hover:border-green-500/30'
                        }`}
                      >
                        <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0">
                          {p.image ? (
                            <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-white font-bold text-base">
                              {(p.name || "?")[0].toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-white truncate">{p.name || "Unknown"}</p>
                          {p.email && (
                            <p className="text-xs text-white/50 truncate">{p.email}</p>
                          )}
                        </div>
                        <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                          isSelected
                            ? 'bg-green-500/20 border-green-400 text-green-400'
                            : 'bg-white/5 border-white/20'
                        }`}>
                          {isSelected && <Check size={16} />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Already Invited */}
            {categorizedParticipants.invited.length > 0 && (
              <div>
                <div className="text-xs font-bold text-white/50 uppercase tracking-wide mb-2.5 px-1">
                  Already Invited ({categorizedParticipants.invited.length})
                </div>
                <div className="space-y-2">
                  {categorizedParticipants.invited.map((p) => {
                    const userId = String(p.userId || p.id);

                    return (
                      <div
                        key={userId}
                        className="flex items-center gap-3 p-3 bg-white/3 border border-white/10 rounded-xl opacity-60 cursor-not-allowed"
                      >
                        <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0">
                          {p.image ? (
                            <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-white font-bold text-base">
                              {(p.name || "?")[0].toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-white truncate">{p.name || "Unknown"}</p>
                          {p.email && (
                            <p className="text-xs text-white/50 truncate">{p.email}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/15 border border-green-500/30 rounded-lg text-green-400 text-xs font-semibold flex-shrink-0">
                          <i className="fa-solid fa-check text-[10px]" />
                          Invited
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Empty State */}
            {categorizedParticipants.available.length === 0 &&
              categorizedParticipants.invited.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Users size={48} className="text-white/20 mb-4" />
                  <p className="text-base font-semibold text-white/80 mb-2">
                    {searchQuery ? "No participants found" : "No participants available"}
                  </p>
                  <p className="text-sm text-white/50">
                    {searchQuery
                      ? "Try adjusting your search"
                      : "All meeting participants have been invited"}
                  </p>
                </div>
              )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-5 border-t border-white/10">
          <div className="flex items-center gap-2 mb-4 text-xs text-white/50">
            <Info size={14} className="text-blue-400 flex-shrink-0" />
            <span>Participants will receive an invite they can accept or decline</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleClose}
              disabled={inviting}
              type="button"
              className="px-5 py-2.5 rounded-xl bg-white/5 border border-white/15 text-white/80 font-semibold text-sm hover:bg-white/10 hover:border-white/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleInvite}
              disabled={inviting || selectedUserIds.length === 0}
              type="button"
              className="ml-auto px-5 py-2.5 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 border border-green-500/50 text-white font-semibold text-sm hover:from-emerald-500 hover:to-green-600 hover:shadow-lg hover:shadow-green-500/30 hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {inviting ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <UserPlus size={16} />
                  Invite {selectedUserIds.length > 0 && `(${selectedUserIds.length})`}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}